import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactElement } from "react";
import { ContentHeader, Field, PercentInput, QueryStatus, useToast } from "@cx/ui";
import type { NfseSettings, NfseSettingsInput } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ENVIRONMENT_LABEL } from "../invoices/labels";
import { ServiceFields } from "./ServiceFields";
import {
  SPECIAL_REGIME_LABEL,
  emptyServiceForm,
  serviceBody,
  serviceFormOf,
  type ServiceForm,
} from "./labels";

const PROVIDER_LABEL: Record<NfseSettings["provider"], string> = {
  sandbox: "Provedor de testes (nenhuma nota vai à prefeitura)",
  nuvemfiscal: "Nuvem Fiscal",
  disabled: "Desligado — a NFS-e não pode ser enviada",
};

type Form = {
  environment: 1 | 2;
  providerMode: NfseSettingsInput["providerMode"];
  specialRegime: NfseSettingsInput["specialRegime"];
  rpsSeries: string;
  rpsNextNumber: string;
  hasService: boolean;
  service: ServiceForm;
  issPct4: number | null;
  issWithheld: boolean;
  pisPct4: number | null;
  cofinsPct4: number | null;
  csllPct4: number | null;
  irPct4: number | null;
  inssPct4: number | null;
  pisCofinsCst: string;
};

const formOf = (s: NfseSettings): Form => ({
  environment: s.environment,
  providerMode: s.providerMode,
  specialRegime: s.specialRegime,
  rpsSeries: s.rpsSeries,
  rpsNextNumber: String(s.rpsNextNumber),
  hasService: s.service !== null,
  service: s.service ? serviceFormOf(s.service) : emptyServiceForm(),
  issPct4: s.issPct4,
  issWithheld: s.issWithheld,
  ...s.retentions,
  pisCofinsCst: s.pisCofinsCst,
});

/**
 * Preferências › Fiscal › Nota fiscal de serviço (T2.8): ambiente, padrão de emissão (Sistema Nacional ou prefeitura),
 * regime especial, série e próximo número do RPS (travados depois do cadastro no provedor), serviço e tributos padrão
 * das NFS-e dos contratos.
 */
export function NfseSettingsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["nfse-settings", companyId];
  const q = useQuery({ queryKey: key, queryFn: () => api<NfseSettings>("/fiscal/nfse-settings") });
  const [f, setF] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (q.data) setF(formOf(q.data));
  }, [q.data]);
  const canEdit = can(perms, "invoices", "update");
  const canEnv = can(perms, "companies", "update");
  const save = useMutation({
    mutationFn: (x: Form) =>
      api<NfseSettings>("/fiscal/nfse-settings", {
        method: "PUT",
        body: JSON.stringify({
          environment: x.environment,
          providerMode: x.providerMode,
          specialRegime: x.specialRegime,
          rpsSeries: x.rpsSeries,
          rpsNextNumber: Number(x.rpsNextNumber),
          service: x.hasService ? serviceBody(x.service) : null,
          issPct4: x.issPct4,
          issWithheld: x.issWithheld,
          retentions: {
            pisPct4: x.pisPct4 ?? 0,
            cofinsPct4: x.cofinsPct4 ?? 0,
            csllPct4: x.csllPct4 ?? 0,
            irPct4: x.irPct4 ?? 0,
            inssPct4: x.inssPct4 ?? 0,
          },
          pisCofinsCst: x.pisCofinsCst,
        }),
      }),
    onSuccess: (s) => {
      setErrors({});
      qc.setQueryData(key, s);
      toast("success", "Configuração da NFS-e salva");
    },
    onError: (e) => {
      setErrors(e instanceof ApiFailure && e.fields ? e.fields : {});
      toast("danger", errorMessage(e));
    },
  });
  const s = q.data;
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => (x ? { ...x, [k]: v } : x));
  return (
    <>
      <ContentHeader
        title="Nota fiscal de serviço"
        breadcrumb={["Preferências", "Fiscal", "Nota fiscal de serviço"]}
      />
      <QueryStatus loading={q.isLoading} error={q.error} />
      {s && f && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canEdit) save.mutate(f);
          }}
        >
          <section className="box card p-3 mb-3" aria-labelledby="nfse-emission">
            <h2 id="nfse-emission" className="h5">
              Emissão da NFS-e
            </h2>
            <p className="text-muted small mb-2">
              Provedor fiscal: <strong>{PROVIDER_LABEL[s.provider]}</strong>.{" "}
              {s.configured ? "" : "Salve esta configuração para emitir NFS-e."}
            </p>
            {f.environment === 1 && s.environment === 2 && (
              <div className="alert alert-warning py-2" role="alert">
                Em produção a NFS-e tem valor fiscal. Confira o certificado digital e o cadastro da empresa na
                prefeitura antes de salvar.
              </div>
            )}
            <div className="row">
              <Field
                label="Ambiente"
                required
                className="col-md-3"
                error={errors.environment}
                hint={canEnv ? undefined : "Mudar o ambiente exige permissão de alterar a empresa."}
              >
                <select
                  className="form-select"
                  value={f.environment}
                  disabled={!canEdit || !canEnv}
                  onChange={(e) => set("environment", Number(e.target.value) as 1 | 2)}
                >
                  <option value={2}>{ENVIRONMENT_LABEL[2]} (testes)</option>
                  <option value={1}>{ENVIRONMENT_LABEL[1]}</option>
                </select>
              </Field>
              <Field label="Padrão de emissão" className="col-md-4" error={errors.providerMode}>
                <select
                  className="form-select"
                  value={f.providerMode}
                  disabled={!canEdit}
                  onChange={(e) => set("providerMode", e.target.value as Form["providerMode"])}
                >
                  <option value="nacional">Sistema Nacional (NFS-e padrão nacional)</option>
                  <option value="padrao">Provedor da prefeitura</option>
                </select>
              </Field>
              <Field label="Regime especial de tributação" className="col-md-5" error={errors.specialRegime}>
                <select
                  className="form-select"
                  value={f.specialRegime}
                  disabled={!canEdit}
                  onChange={(e) => set("specialRegime", Number(e.target.value) as Form["specialRegime"])}
                >
                  {([0, 1, 2, 3, 4, 5, 6] as const).map((k) => (
                    <option key={k} value={k}>
                      {SPECIAL_REGIME_LABEL[k]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Série do RPS"
                className="col-md-3"
                error={errors.rpsSeries}
                hint={s.registered ? "Já cadastrada no provedor." : undefined}
              >
                <input
                  value={f.rpsSeries}
                  maxLength={5}
                  disabled={!canEdit || s.registered}
                  onChange={(e) => set("rpsSeries", e.target.value)}
                />
              </Field>
              <Field
                label="Próximo número do RPS"
                className="col-md-3"
                error={errors.rpsNextNumber}
                hint={s.registered ? "O provedor controla a numeração." : "Sem pular números."}
              >
                <input
                  inputMode="numeric"
                  value={f.rpsNextNumber}
                  disabled={!canEdit || s.registered}
                  onChange={(e) => set("rpsNextNumber", e.target.value.replace(/\D/g, "").slice(0, 9))}
                />
              </Field>
            </div>
          </section>
          <section className="box card p-3 mb-3" aria-labelledby="nfse-taxes">
            <h2 id="nfse-taxes" className="h5">
              Tributos padrão
            </h2>
            <div className="row">
              <Field label="Alíquota do ISS" className="col-md-3" error={errors.issPct4}>
                <PercentInput value={f.issPct4} disabled={!canEdit} onChange={(v) => set("issPct4", v)} />
              </Field>
              <Field label="CST do PIS/COFINS" className="col-md-3" error={errors.pisCofinsCst}>
                <input
                  inputMode="numeric"
                  value={f.pisCofinsCst}
                  maxLength={2}
                  disabled={!canEdit}
                  onChange={(e) => set("pisCofinsCst", e.target.value.replace(/\D/g, ""))}
                />
              </Field>
              <div className="col-md-6 mb-3 d-flex align-items-end">
                <div className="form-check">
                  <input
                    id="nfse-settings-withheld"
                    type="checkbox"
                    className="form-check-input"
                    checked={f.issWithheld}
                    disabled={!canEdit}
                    onChange={(e) => set("issWithheld", e.target.checked)}
                  />
                  <label htmlFor="nfse-settings-withheld" className="form-check-label">
                    ISS retido pelo tomador
                  </label>
                </div>
              </div>
            </div>
            <div className="row">
              {(
                [
                  ["pisPct4", "PIS retido"],
                  ["cofinsPct4", "COFINS retida"],
                  ["csllPct4", "CSLL retida"],
                  ["irPct4", "IR retido"],
                  ["inssPct4", "INSS retido"],
                ] as const
              ).map(([k, label]) => (
                <Field key={k} label={label} className="col-6 col-md" error={errors[`retentions.${k}`]}>
                  <PercentInput value={f[k]} disabled={!canEdit} onChange={(v) => set(k, v)} />
                </Field>
              ))}
            </div>
          </section>
          <section className="box card p-3 mb-3" aria-labelledby="nfse-service">
            <h2 id="nfse-service" className="h5">
              Serviço padrão dos contratos
            </h2>
            <div className="form-check mb-3">
              <input
                id="nfse-has-service"
                type="checkbox"
                className="form-check-input"
                checked={f.hasService}
                disabled={!canEdit}
                onChange={(e) => set("hasService", e.target.checked)}
              />
              <label htmlFor="nfse-has-service" className="form-check-label">
                Usar um serviço padrão nas NFS-e das cobranças (contrato sem serviço próprio)
              </label>
            </div>
            {f.hasService && (
              <ServiceFields
                value={f.service}
                errors={errors}
                disabled={!canEdit}
                onChange={(v) => set("service", v)}
              />
            )}
          </section>
          {canEdit && (
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              Salvar configuração
            </button>
          )}
        </form>
      )}
    </>
  );
}
